import { Sparkles } from 'lucide-react';
import { useRef, useState, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';

import { setResume } from '../../redux/Slices/ResumeSlice';
import { useUploadResumeMutation } from '../../services/resumeBuilderApi';
import Button from '../Common/Button';

const MAX_SIZE_MB = 5; // tweak if BE has a different limit

const UploadResume = ({ onNext }: { onNext: () => void }) => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [uploadResume, { isLoading }] = useUploadResumeMutation();

  const validatePdf = (selected: File): string => {
    const isPdfMime = selected.type === 'application/pdf';
    const isPdfExt = selected.name.toLowerCase().endsWith('.pdf');
    if (!isPdfMime || !isPdfExt) {
      return 'Only PDF files are allowed.';
    }
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      return `File size must be under ${MAX_SIZE_MB} MB.`;
    }
    return '';
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError('');
    const selected = e.target.files?.[0];
    if (!selected) return;

    const err = validatePdf(selected);
    if (err) {
      setFile(null);
      setError(err);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(selected);
  };

  const handleBrowseClick = () => {
    inputRef.current?.click();
  };

  const handleRemove = () => {
    setFile(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleContinue = async () => {
    if (!file) {
      setError('Please upload a PDF resume before continuing.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      const response = await uploadResume(formData).unwrap();
      dispatch(setResume(response?.data));
      onNext();
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Failed to upload resume. Please try again.');
    }
  };

  return (
    <div className='flex w-full flex-col items-center justify-center p-6'>
      <div
        className={`w-full max-w-xl rounded-lg border-2 border-dashed border-primary bg-white p-8 text-center ${
          isLoading ? 'pointer-events-none opacity-70' : ''
        }`}
      >
        <p className='mb-2 text-lg font-semibold text-primary'>
          Upload your resume
        </p>
        <p className='mb-4 text-sm text-gray-500'>
          Only PDF files are supported (max {MAX_SIZE_MB} MB).
        </p>

        <input
          ref={inputRef}
          type='file'
          accept='application/pdf,.pdf'
          className='hidden'
          onChange={handleFileChange}
        />

        {!file ? (
          <Button
            variant='outlined'
            label='Browse PDF'
            handleClickBtn={handleBrowseClick}
          />
        ) : (
          <div className='flex flex-col items-center gap-2'>
            <p className='text-sm text-gray-700'>
              <i className='fa fa-file-pdf-o mr-2 text-primary' />
              {file.name}{' '}
              <span className='text-gray-400'>
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </p>
            <div className='flex gap-2'>
              <Button
                variant='outlined-danger'
                label='Remove'
                handleClickBtn={handleRemove}
                disabled={isLoading}
              />
              <Button
                variant='outlined'
                label='Replace'
                handleClickBtn={handleBrowseClick}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        {error && <p className='mt-3 text-sm text-red-600'>{error}</p>}
      </div>

      {isLoading && (
        <div
          role='status'
          aria-live='polite'
          className='mt-6 flex w-full max-w-xl items-start gap-3 rounded-xl border border-primary/30 bg-gamma p-4 text-left shadow-sm'
        >
          <div className='mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
            <Sparkles size={16} className='animate-pulse' />
          </div>
          <div className='flex-1'>
            <p className='flex items-center gap-2 text-sm font-semibold text-primary'>
              Extracting data from your resume
              <i className='fa fa-spinner fa-spin text-xs' />
            </p>
            <p className='mt-1 text-xs text-gray-600'>
              Hang tight — we&apos;re reading your PDF and structuring the
              content for the editor. Please don&apos;t close this window or
              take any other actions until we&apos;re done.
            </p>
          </div>
        </div>
      )}

      <div className='mt-6'>
        <Button
          variant='contained'
          label={isLoading ? 'Extracting…' : 'Continue'}
          handleClickBtn={handleContinue}
          disabled={!file || isLoading}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default UploadResume;
